import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import _, { PropertyPath } from 'lodash';
import React from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle as Required,
  faCheckCircle as Check,
} from '@fortawesome/pro-light-svg-icons';
// NK
import { combineClasses } from '../../utils/styling';
import { useNkStyles } from '../../infrastructure/theme';
import { BaseTextFieldProps } from '@material-ui/core/TextField/TextField';

interface NkSelectProps {
  label?: string;
  name: string;
  style?: React.CSSProperties;
  variant?: BaseTextFieldProps['variant'];
  items: Array<any>;
  fullWidth?: boolean;
  wrapperStyle?: any;
  itemAsItemProp?: boolean;
  defaultSelectLabel?: string;
  showSelect?: boolean;
  margin?: 'normal' | 'dense' | 'none';
  wrapper?: boolean;
  onChange: (event: any) => void;
  id?: string;
  multiple?: boolean;
  itemPropName?: PropertyPath;
  itemTitlePropName?: string;
  disabled?: boolean;
  required?: boolean;
  isValid?: boolean;
  errorText?: string;
  inputClasses?: any;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  value: BaseTextFieldProps['value'];
  children?: React.ReactNodeArray;
}

const assertPropertyPath = (
  itemPropName?: PropertyPath,
  name?: string,
): PropertyPath => {
  if (typeof itemPropName === 'undefined') {
    throw TypeError(
      `Assertion failed for PropertyPath ${itemPropName} (${name})`,
    );
  }

  return itemPropName as PropertyPath;
};

const NKSelect = (props: NkSelectProps) => {
  const classes = useNkStyles();
  const _fullWidth = props.fullWidth !== undefined ? props.fullWidth : true;
  const _margin = props.margin || 'dense';
  const _wrapper = props.wrapper !== undefined ? props.wrapper : true;

  if (!props.items) {
    return <div />;
  }

  const component = (
    <TextField
      variant={props.variant}
      style={props.style}
      fullWidth={_fullWidth}
      margin={_margin}
      disabled={props.disabled}
      name={props.name}
      id={props.name}
      label={props.label}
      value={props.value}
      onChange={props.onChange}
      select
      helperText={props.errorText}
      InputProps={{
        classes: props.inputClasses,
      }}
      SelectProps={{
        multiple: props.multiple,
        open: props.open,
        onOpen: props.onOpen,
        onClose: props.onClose,
      }}
      FormHelperTextProps={{
        className:
          !props.required || props.isValid
            ? combineClasses([classes.validFieldText, classes.fieldHelperText])
            : combineClasses([classes.errorText, classes.fieldHelperText]),
      }}
    >
      {props.showSelect ? (
        <MenuItem value={''}>{props.defaultSelectLabel}</MenuItem>
      ) : null}
      {props.items.map((item: any, i: any) => (
        <MenuItem
          key={i}
          value={
            props.itemAsItemProp
              ? JSON.stringify(item)
              : _.get(
                  item,
                  assertPropertyPath(props.itemPropName, 'itemPropName'),
                )
          }
        >
          {props.itemAsItemProp
            ? item
            : _.get(
                item,
                assertPropertyPath(
                  props.itemTitlePropName,
                  'itemTitlePropName',
                ),
              )}
        </MenuItem>
      ))}
    </TextField>
  );

  return _wrapper ? (
    <div style={props.wrapperStyle}>{component}</div>
  ) : (
    component
  );
};

NKSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  multiple: PropTypes.bool,
  itemPropName: PropTypes.string,
  itemAsItemProp: PropTypes.bool,
  itemTitlePropName: PropTypes.string,
  showSelect: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  margin: PropTypes.oneOf(['normal', 'dense', 'none']),
  wrapper: PropTypes.bool,
  wrapperStyle: PropTypes.object,
  style: PropTypes.object,
  required: PropTypes.bool,
  isValid: PropTypes.bool,
  defaultSelectLabel: PropTypes.string,
  variant: PropTypes.string,
  errorText: PropTypes.string,
  inputClasses: PropTypes.object,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

export default React.memo(NKSelect);
